import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { Loader2, Sparkles, Copy, Check, Film } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { aiVideoScript } from "@/lib/ai";
import { fetchCampaigns } from "@/lib/api";

const DURATIONS = [
  { s: 30,  label: "30s" },
  { s: 60,  label: "60s" },
  { s: 120, label: "2 min" },
  { s: 300, label: "5 min" },
];

const SECTIONS = ["hook", "introduction", "problem", "solution", "demo", "founder_story", "funding_ask", "closing"];

const Section = ({ id, data, testid }) => {
  const [copied, setCopied] = useState(false);
  if (!data) return null;
  const text = `[${id.toUpperCase()} · ${data.seconds}s]\nVoiceover: ${data.voiceover}\nOn-screen: ${data.on_screen}\nB-roll: ${data.b_roll}`;
  const copy = () => navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); toast.success("Copied"); });
  return (
    <div className="rounded-2xl border border-warm-200 bg-white p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-saffron-700">{id.replace(/_/g, " ")}</span>
          <span className="text-[11px] font-semibold text-navy-500">{data.seconds}s</span>
        </div>
        <button onClick={copy} className="inline-flex items-center gap-1 rounded-full border border-warm-200 h-7 px-2.5 text-[11px] font-semibold text-navy-700 hover:border-saffron-300 hover:text-saffron-700">
          {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
        </button>
      </div>
      <div className="mt-3 space-y-2 text-sm">
        <div><span className="text-[10px] uppercase tracking-wider font-bold text-navy-500">VOICEOVER</span><p className="text-navy-800 leading-[1.65]">{data.voiceover}</p></div>
        <div><span className="text-[10px] uppercase tracking-wider font-bold text-navy-500">ON-SCREEN</span><p className="text-navy-700">{data.on_screen}</p></div>
        <div><span className="text-[10px] uppercase tracking-wider font-bold text-navy-500">B-ROLL</span><p className="text-navy-700 italic">{data.b_roll}</p></div>
      </div>
    </div>
  );
};

const VideoScript = () => {
  const { user, loading: authLoading, requireAuth } = useAuth();
  const [duration, setDuration] = useState(60);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [script, setScript] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (!authLoading && !user) requireAuth("Sign in to generate a video script."); }, [user, authLoading, requireAuth]);
  useEffect(() => { if (user) fetchCampaigns({ mine: true, limit: 25 }).then((r) => { const items = r.items || r || []; setCampaigns(items); if (items.length) setSelectedId(items[0].id); }).catch(() => {}); }, [user]);

  const selected = campaigns.find((c) => c.id === selectedId);
  const run = async () => {
    if (!selected) { toast.error("Pick a campaign first"); return; }
    setLoading(true); setScript(null);
    try {
      const snap = { title: selected.title, tagline: selected.tagline, story: selected.story, goal: selected.goal, category: selected.category, rewards: selected.rewards || [] };
      const r = await aiVideoScript({ duration_seconds: duration, campaign: snap });
      setScript(r.data);
    } catch (e) { toast.error(e.message || "Failed"); } finally { setLoading(false); }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10" data-testid="video-script-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-saffron-700"><Film className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-[0.2em]">Video Script</span></div>
          <h1 className="mt-1 font-display text-3xl md:text-4xl font-bold tracking-tight text-navy-800">Shoot-ready pitch scripts.</h1>
          <p className="mt-2 text-navy-600 max-w-2xl">Voiceover, on-screen text, and b-roll suggestions — split into 8 sections that add up to your target duration.</p>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-warm-200 bg-white p-4 flex flex-wrap gap-2" data-testid="vs-duration-picker">
        {DURATIONS.map((d) => (
          <button key={d.s} onClick={() => setDuration(d.s)} data-testid={`vs-duration-${d.s}`} className={`rounded-full px-4 h-9 text-sm font-semibold transition-colors ${duration === d.s ? "bg-navy-800 text-white" : "text-navy-600 hover:text-navy-800 hover:bg-warm-100"}`}>{d.label}</button>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 rounded-2xl border border-warm-200 bg-white p-6 space-y-4">
          <div>
            <label className="text-xs font-semibold text-navy-700 uppercase tracking-wide">Campaign</label>
            <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)} data-testid="vs-campaign" className="mt-1.5 w-full h-11 rounded-2xl border border-warm-200 bg-white px-3 text-sm focus:outline-none focus:border-saffron-500 focus:ring-4 focus:ring-saffron-500/15 transition">
              {campaigns.map((c) => <option key={c.id} value={c.id}>{c.title || "(untitled)"}</option>)}
            </select>
          </div>
          <button onClick={run} disabled={loading || !selected} data-testid="vs-run" className="btn-saffron w-full inline-flex items-center justify-center gap-2 rounded-2xl h-11 font-semibold disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {loading ? "Writing…" : `Generate ${DURATIONS.find((d) => d.s === duration)?.label} script`}
          </button>
          {script?.music_direction && (<div className="rounded-2xl bg-cream-100 border border-warm-200 p-4 text-sm text-navy-700"><div className="text-[10px] font-bold uppercase tracking-[0.16em] text-saffron-700 mb-1">Music direction</div>{script.music_direction}</div>)}
        </div>

        <div className="lg:col-span-8 space-y-3">
          {!script && loading && (<div className="rounded-2xl border border-warm-200 bg-white p-12 text-center"><Loader2 className="h-8 w-8 mx-auto animate-spin text-saffron-600" /><p className="mt-3 text-navy-500">Writing your script…</p></div>)}
          {!script && !loading && (<div className="rounded-2xl border border-warm-200 bg-cream-100 p-10 text-center text-sm text-navy-600">Pick a duration and campaign to generate your script.</div>)}
          {script && SECTIONS.map((k) => script[k] && <Section key={k} id={k} data={script[k]} testid={`vs-${k}`} />)}
          {script?.shot_list?.length > 0 && (
            <div className="rounded-2xl border border-warm-200 bg-white p-5">
              <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-saffron-700 mb-2">Shot list</div>
              <ul className="space-y-1 text-sm text-navy-700">{script.shot_list.map((s, i) => <li key={i} className="flex gap-2"><span className="text-saffron-600">•</span>{s}</li>)}</ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoScript;
